import type { CmmdBoardTask, CmmdProjectMilestone } from "@cmmd-forge/contracts";

const NO_MILESTONE_GROUP = "milestone:none";

export interface TaskMilestoneGroup {
  readonly key: string;
  readonly label: string;
  readonly order: number;
  readonly milestoneId: number | null;
}

function milestoneGroupKey(milestoneId: number | null): string {
  return milestoneId === null ? NO_MILESTONE_GROUP : `milestone:${milestoneId}`;
}

export function milestoneIdFromGroupKey(key: string): number | null {
  if (key === NO_MILESTONE_GROUP) return null;
  const value = Number(key.replace(/^milestone:/, ""));
  return Number.isInteger(value) && value > 0 ? value : null;
}

export function taskMilestoneGroups(
  milestones: readonly CmmdProjectMilestone[],
): readonly TaskMilestoneGroup[] {
  const sorted = milestones.toSorted((left, right) => {
    const sortDelta =
      (left.sortIndex ?? Number.MAX_SAFE_INTEGER) - (right.sortIndex ?? Number.MAX_SAFE_INTEGER);
    if (sortDelta !== 0) return sortDelta;
    return (left.endDate ?? "~").localeCompare(right.endDate ?? "~");
  });
  return [
    ...sorted.map((milestone, index) => ({
      key: milestoneGroupKey(milestone.id),
      label: milestone.title,
      order: index,
      milestoneId: milestone.id,
    })),
    {
      key: NO_MILESTONE_GROUP,
      label: "No milestone",
      order: sorted.length,
      milestoneId: null,
    },
  ];
}

export function taskMilestoneGroupResolver(
  groups: readonly TaskMilestoneGroup[],
): (task: CmmdBoardTask) => TaskMilestoneGroup {
  const groupByMilestoneId = new Map(groups.map((group) => [group.milestoneId, group]));
  const fallback = groupByMilestoneId.get(null) ?? {
    key: NO_MILESTONE_GROUP,
    label: "No milestone",
    order: groups.length,
    milestoneId: null,
  };
  return (task) => groupByMilestoneId.get(task.milestoneId) ?? fallback;
}

export function tasksByMilestoneGroup(
  tasks: readonly CmmdBoardTask[],
  groups: readonly TaskMilestoneGroup[],
): ReadonlyMap<string, readonly CmmdBoardTask[]> {
  const groupedTasks = new Map<string, CmmdBoardTask[]>(groups.map((group) => [group.key, []]));
  const groupForTask = taskMilestoneGroupResolver(groups);
  for (const task of tasks) groupedTasks.get(groupForTask(task).key)?.push(task);
  return groupedTasks;
}
